import React, { useEffect } from "react";
import { Box } from "@mui/material";
import AudioTranscriber from "./components/AudioTranscriber";
import Homepage from "./components/home/HomePage";
import { ToastContainer } from "react-toastify";
import { Routes, Route, Navigate, useNavigate } from "react-router-dom";
import StartForFree from "./components/start-for-free/StartForFree";
import TermsAndConditions from "./components/terms-and-conditions/TermsAndConditions";
import Settings from "./components/settings/Settings";
import BillingHistoryPage from "./billing-history/BillingHistoryPage";
import FeedbackPage from "./components/feedback/FeedbackPage";
import TermsAndConditionsDarkPage from "./components/terms-and-conditions/TermsAndConditionsDark";
import TutorialPage from "./components/tutorial/TutorialPage";
import NavBar from "./components/navbar/NavBar";
import { ClerkProvider, useAuth, SignIn } from "@clerk/clerk-react";
import ClerkAuthProvider from "./components/auth/ClerkAuthProvider";
import { CircularProgress } from "@mui/material";
import SubscribeFormPage from "./pages/subscription/SubscriptionPage";
import SubscriptionSuccessPage from "./pages/subscription/SuccessPage";
import Login from "./components/login/Login";
import SignUpPage from "./components/sign-up/SignUpPage";
import PrivacyPolicy from "./components/privacy-policy/PrivacyPolicy";
import TermsOfService from "./components/terms-of-service/TermsOfService";
import Contact from "./components/contact/Contact";
import DirectProSignupPage from "./pages/DirectProSignupPage";

const clerkPubKey = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY || '';

if (!clerkPubKey) {
  throw new Error("Missing Clerk Publishable Key");
}

// Componente para manejar el callback de SSO
const SSOCallback = () => {
  const navigate = useNavigate();
  
  useEffect(() => {
    // Damos tiempo a Clerk para procesar la autenticación y luego redirigimos
    const timer = setTimeout(() => {
      navigate('/dashboard');
    }, 2000);
    
    return () => clearTimeout(timer);
  }, [navigate]);
  
  return (
    <Box 
      display="flex" 
      justifyContent="center" 
      alignItems="center" 
      flexDirection="column"
      height="100vh"
    >
      <CircularProgress />
      <Box mt={2}>Completando autenticación...</Box>
    </Box>
  );
};

// Componente para proteger rutas
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isSignedIn, isLoaded } = useAuth();
  
  if (!isLoaded) {
    // Puedes mostrar un spinner o indicador de carga mientras Clerk verifica la autenticación
    return <div>Loading authentication...</div>;
  }
  
  if (!isSignedIn) {
    // Redirigir al usuario a la página de inicio de sesión si no está autenticado
    return <Navigate to="/login" replace />;
  }
  
  return <>{children}</>;
};

function App() {
  return (
    <ClerkProvider publishableKey={clerkPubKey}>
      <ClerkAuthProvider>
        <Box width={"100%"}>
          <ToastContainer />
          <Routes>
            {/* Rutas públicas */}
            <Route path="/" element={<Homepage />} />
            <Route path="/start" element={<StartForFree />} />
            <Route path="/terms" element={<TermsAndConditions />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/contact" element={<Contact />} />
            
            {/* Nueva ruta para suscripción Pro directa */}
            <Route 
              path="/direct-pro-signup" 
              element={
                <DirectProSignupPage />
              } 
            />
            
            {/* Rutas de autenticación de Clerk */}
            <Route 
              path="/login/*" 
              element={<Login />} 
            />
            <Route 
              path="/signup/*" 
              element={<SignUpPage />} 
            />
            <Route path="/login/sso-callback" element={<SSOCallback />} />
            <Route path="/sso-callback" element={<SSOCallback />} />
            
            {/* Rutas protegidas */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <NavBar />
                  <AudioTranscriber />
                </ProtectedRoute>
              }
            />
            <Route
              path="/subscription/:planId"
              element={
                <ProtectedRoute>
                  <NavBar />
                  <SubscribeFormPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/subscription/success"
              element={
                <ProtectedRoute>
                  <NavBar />
                  <SubscriptionSuccessPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <NavBar />
                  <Box>Profile page will be replaced by Clerk's user profile</Box>
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <NavBar />
                  <Settings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/feedback"
              element={
                <ProtectedRoute>
                  <NavBar />
                  <FeedbackPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tos"
              element={
                <ProtectedRoute>
                  <NavBar />
                  <TermsAndConditionsDarkPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tutorial"
              element={
                <ProtectedRoute>
                  <NavBar />
                  <TutorialPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/view/billing-history"
              element={
                <ProtectedRoute>
                  <NavBar />
                  <BillingHistoryPage />
                </ProtectedRoute>
              }
            />
          </Routes>
        </Box>
      </ClerkAuthProvider>
    </ClerkProvider>
  );
}

export default App;
